import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { eventService } from '../../../services/event.service';
import { tagService } from '../../../services/tags.service';
import { PERMISSIONS } from '../../../shared/permission';
import PermissionChecker from '../../UI/PermissionChecker/PermissionChecker';
import Button from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import Select from '../../UI/Select/Select';
import MultiSelectAutocomplete from '../../UI/MultiSelectAutocomplete/MultiSelectAutocomplete';
import Alert from '../../UI/Alert/Alert';
import { toast } from 'react-toastify';
import styles from './EventCreateForm.module.css';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const validateUrl = (url) => {
    if (!url) return true;
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

const emptyState = () => ({ value: '', error: null });

function EventCreateForm({ isOpen, onClose, onSuccess }) {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');
    const fileInputRef = useRef(null);

    const [title, setTitle] = useState(emptyState());
    const [description, setDescription] = useState(emptyState());
    const [organizerLink, setOrganizerLink] = useState(emptyState());
    const [startDate, setStartDate] = useState(emptyState());
    const [endDate, setEndDate] = useState(emptyState());
    const [status, setStatus] = useState({ value: 'draft', error: null });
    const [file, setFile] = useState({ value: null, error: null });
    const [tags, setTags] = useState({ value: [], error: null });
    const [tagsList, setTagsList] = useState([]);

    const statusOptions = [
        { value: 'draft', label: t('events.status_draft') },
        { value: 'published', label: t('events.status_published') },
        { value: 'archived', label: t('events.status_archived') },
    ];
    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        tagService.getTagsAll()
            .then((data) => setTagsList(data?.tags ?? data ?? []))
            .catch(() => setTagsList([]));
    }, [isOpen]);

    if (!isOpen) return null;

    const reset = () => {
        setTitle(emptyState());
        setDescription(emptyState());
        setOrganizerLink(emptyState());
        setStartDate(emptyState());
        setEndDate(emptyState());
        setStatus({ value: 'draft', error: null });
        setFile({ value: null, error: null });
        setTags({ value: [], error: null });
        setGeneralError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGeneralError(null);

        if (organizerLink.value && !validateUrl(organizerLink.value)) {
            setOrganizerLink((prev) => ({ ...prev, error: { code: 'format.url' } }));
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (endDate.value && new Date(endDate.value) < today) {
            setEndDate((prev) => ({ ...prev, error: { code: 'end_date_cannot_be_in_the_past' } }));
            return;
        }

        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('title', title.value);
            formData.append('description', description.value);
            formData.append('start_date', startDate.value);
            formData.append('end_date', endDate.value);
            if (status.value) formData.append('status', status.value);
            if (organizerLink.value) formData.append('organizer_link', organizerLink.value);
            if (file.value) formData.append('file', file.value);
            const tagsArray = tags.value.map((tag) => tag?.code ?? tag?.value ?? tag);
            if (tagsArray.length > 0) formData.append('tags', JSON.stringify(tagsArray));

            await eventService.createEvent(formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(tValidation('event_created_successfully'));
            reset();
            onClose();
            onSuccess?.();
        } catch (error) {
            const errorEntity = error?.payload?.error;
            if (errorEntity && Array.isArray(errorEntity)) {
                errorEntity.forEach((errorItem) => {
                    const field = errorItem.path?.[0];
                    if (field === 'title') setTitle((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'description') setDescription((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'organizer_link') setOrganizerLink((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'start_date') setStartDate((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'end_date') setEndDate((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'status') setStatus((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'file') setFile((prev) => ({ ...prev, error: errorItem }));
                });
            } else if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity);
                setGeneralError(message);
                toast.error(message);
            } else {
                toast.error(tValidation('something_went_wrong'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PermissionChecker permissions={[PERMISSIONS.EVENTS.CREATE]}>
            <div className={styles.overlay}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h2 className={styles.title}>{t('events.create')}</h2>
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            ×
                        </button>
                    </div>

                    {generalError && (
                        <Alert type="error">
                            <p>{generalError}</p>
                        </Alert>
                    )}

                    <form id="event-create-form" className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t('events.title_label')} *</label>
                            <Input
                                type="text"
                                placeholder={t('events.title_placeholder')}
                                value={title.value}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    let error = null;
                                    if (val.length > 200) {
                                        error = { code: 'title_too_long', params: { maximum: 200 } };
                                    }
                                    setTitle({ value: val, error });
                                }}
                                error={title.error}
                                disabled={isLoading}
                            />
                            <div className={`${styles.charCount} ${title.value.length > 200 ? styles.charCountError : ''}`}>
                                {title.value.length} / 200
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t('events.description_label')} *</label>
                            <textarea
                                className={`${styles.textarea} ${description.error ? styles.textareaError : ''}`}
                                placeholder={t('events.description_placeholder')}
                                value={description.value}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    let error = null;
                                    if (val.length > 5000) {
                                        error = { code: 'description_too_long', params: { maximum: 5000 } };
                                    }
                                    setDescription({ value: val, error });
                                }}
                                rows={5}
                                disabled={isLoading}
                            />
                            <div className={`${styles.charCount} ${description.value.length > 5000 ? styles.charCountError : ''}`}>
                                {description.value.length} / 5000
                            </div>
                            {description.error && (
                                <p className={styles.errorText}>
                                    {/string\.min|string\.invalid/.test(description.error?.code ?? '')
                                        ? tValidation('field_required')
                                        : tValidation(description.error?.code || '').replace('{{toReplace}}', description.error?.params?.maximum ?? description.error?.params?.minimum)}
                                </p>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t('events.organizer_link_label')}</label>
                            <Input
                                type="url"
                                placeholder={t('events.organizer_link_placeholder')}
                                value={organizerLink.value}
                                onChange={(e) => setOrganizerLink({ value: e.target.value, error: null })}
                                error={organizerLink.error}
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.row}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>{t('events.start_date_label')} *</label>
                                <Input
                                    type="date"
                                    value={startDate.value}
                                    onChange={(e) => setStartDate({ value: e.target.value, error: null })}
                                    error={startDate.error}
                                    disabled={isLoading}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>{t('events.end_date_label')} *</label>
                                <Input
                                    type="date"
                                    value={endDate.value}
                                    onChange={(e) => setEndDate({ value: e.target.value, error: null })}
                                    error={endDate.error}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t('events.status_label')}</label>
                            <Select
                                options={statusOptions}
                                value={status.value}
                                onChange={(e) => setStatus({ value: e.target.value, error: null })}
                                error={status.error}
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t('events.cover_label')}</label>
                            <div className={styles.fileInputWrapper}>
                                <input
                                    ref={fileInputRef}
                                    id="event-cover-file"
                                    type="file"
                                    className={styles.fileInputHidden}
                                    onChange={(e) => {
                                        const f = e.target.files?.[0] ?? null;
                                        if (f) {
                                            if (!ALLOWED_IMAGE_TYPES.includes(f.type)) {
                                                setFile({ value: null, error: { code: 'file_type_invalid' } });
                                                e.target.value = '';
                                                return;
                                            }
                                            if (f.size > MAX_FILE_SIZE) {
                                                setFile({ value: null, error: { code: 'file_size_exceeded' } });
                                                e.target.value = '';
                                                return;
                                            }
                                        }
                                        setFile({ value: f, error: null });
                                    }}
                                    accept={ALLOWED_IMAGE_TYPES.join(',')}
                                    disabled={isLoading}
                                />
                                <span className={styles.fileName}>
                                    {file.value?.name || t('events.cover_placeholder')}
                                </span>
                                <label className={styles.fileButton} htmlFor="event-cover-file">
                                    {t('events.cover_button')}
                                </label>
                            </div>
                            <p className={styles.hint}>{t('events.cover_hint')}</p>
                            {file.error && (
                                <p className={styles.errorText}>{tValidation(file.error?.code || '')}</p>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t('events.tags_label')}</label>
                            <MultiSelectAutocomplete
                                options={tagsList}
                                value={tags.value}
                                onChange={(values) => setTags({ value: values, error: null })}
                                placeholder={t('ideas.tags_placeholder')}
                                error={tags.error}
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.buttons}>
                            <Button variant="secondary" type="button" onClick={handleClose} disabled={isLoading}>
                                {t('events.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isLoading || !!title.error || !!description.error}
                            >
                                {t('events.create')}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </PermissionChecker>
    );
}

export default EventCreateForm;
